import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { SplitTaskDialog } from './SplitTaskDialog';
import { useCanWork } from '@/features/workspaces/canWork';

/**
 * The move button, its reason when it is disabled, and the naming popup, for a
 * list of subtasks with some ticked (spec 137 US3). Shared by the drawer's
 * list and the run page's, which differ only in their rows.
 *
 * Moving every subtask would leave the original task with nothing to wait on,
 * so the button is disabled then and says why.
 */
function SplitSelectionControlInner({
  workspaceId,
  parent,
  selectedIds,
  total,
  onSplit,
}: {
  workspaceId: string;
  parent: { id: string; title: string };
  selectedIds: string[];
  /** How many subtasks the list shows, ticked or not. */
  total: number;
  onSplit: () => void;
}) {
  const [open, setOpen] = useState(false);
  const takesAll = selectedIds.length === total;

  return (
    <>
      {selectedIds.length > 0 ? (
        <div className="mt-2 flex flex-col items-start gap-1">
          <Button
            size="sm"
            variant="outline"
            disabled={takesAll}
            onClick={() => setOpen(true)}
          >
            Move to new task ({selectedIds.length})
          </Button>
          {takesAll ? (
            <p className="text-muted-foreground text-xs">
              Leave at least one subtask with {parent.title}
            </p>
          ) : null}
        </div>
      ) : null}
      <SplitTaskDialog
        workspaceId={workspaceId}
        parent={parent}
        childTaskIds={selectedIds}
        open={open && selectedIds.length > 0}
        onOpenChange={setOpen}
        onSplit={onSplit}
      />
    </>
  );
}

/** Splitting creates tasks: members only (spec 189). */
export function SplitSelectionControl(
  props: Parameters<typeof SplitSelectionControlInner>[0],
) {
  return useCanWork() ? <SplitSelectionControlInner {...props} /> : null;
}
